import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faTimesCircle, faSpinner, faUpload, faEnvelope,
  faIdCard, faFileAlt, faMapMarkerAlt, faExclamationTriangle,
  faClock, faShieldAlt, faBuilding, faUser, faCamera, faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { kycAPI } from '../services/api';
import './KYCVerification.css';

const KYCVerification = ({ userType = 'seller', email = '', isDarkMode = false, onComplete }) => {
  // Verification status state
  const [verificationStatus, setVerificationStatus] = useState({
    email: { status: 'pending', verified: false },
    identity: { status: 'not_started', verified: false },
    business: { status: 'not_started', verified: false },
    address: { status: 'not_started', verified: false }
  });

  // Form states
  const [activeStep, setActiveStep] = useState('email');
  const [isLoading, setIsLoading] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [toast, setToast] = useState(null);

  // Email verification state
  const [emailCode, setEmailCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Identity verification state
  const [identityData, setIdentityData] = useState({
    idType: '',
    idNumber: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    idFrontImage: null,
    idBackImage: null,
    selfieImage: null
  });

  // Business verification state (for business accounts)
  const [businessData, setBusinessData] = useState({
    businessType: '',
    registrationNumber: '',
    taxId: '',
    businessDocument: null,
    proofOfOwnership: null
  });

  // Address verification state
  const [addressData, setAddressData] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Nigeria',
    proofDocument: null
  });

  // Countdown timer for resend email
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Check verification status on mount
  useEffect(() => {
    checkVerificationStatus();
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const response = await kycAPI.getStatus();

      if (response && response.data) {
        const { steps, verification_status } = response.data;
        
        setVerificationStatus({
          email: { 
            status: steps.email?.verified ? 'verified' : 'pending', 
            verified: steps.email?.verified 
          },
          identity: { 
            status: steps.nin?.status || 'not_started', 
            verified: steps.nin?.verified 
          },
          business: { 
            status: steps.business?.status || 'not_started', 
            verified: steps.business?.verified 
          },
          address: { 
            status: steps.address?.verified ? 'verified' : 'not_started', 
            verified: steps.address?.verified 
          }
        });

        // Set active step to the first unverified step
        if (!steps.email?.verified) {
          setActiveStep('email');
        } else if (!steps.nin?.verified) {
          setActiveStep('identity');
        } else if (userType === 'seller' && !steps.business?.verified) {
          setActiveStep('business');
        } else if (!steps.address?.verified) {
          setActiveStep('address');
        }
      }
    } catch (error) {
      // Don't show error on initial load
    }
  };

  // Email Verification Functions
  const sendEmailVerification = async () => {
    setIsLoading(true);
    try {
      await kycAPI.sendEmailVerification(email);

      setEmailSent(true);
      setCountdown(60);
      setToast({ message: 'Verification code sent to your email!', type: 'success' });

      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      setToast({ message: error.message || 'Failed to send verification code', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmailCode = async () => {
    if (!emailCode || emailCode.length !== 6) {
      setToast({ message: 'Please enter a valid 6-digit code', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    setIsLoading(true);
    try {
      await kycAPI.verifyEmail(emailCode);

      setVerificationStatus(prev => ({
        ...prev,
        email: { status: 'verified', verified: true }
      }));

      setToast({ message: 'Email verified successfully!', type: 'success' });
      setTimeout(() => {
        setToast(null);
        setActiveStep('identity');
      }, 2000);
    } catch (error) {
      setToast({ message: error.message || 'Invalid verification code', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Identity Verification Functions
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB UI limit
  // Per-file target: 400KB so 3 files combined stay well under any nginx limit
  const PER_FILE_TARGET = 400 * 1024;

  // Compress an image using Canvas — scales down + reduces JPEG quality
  const compressImage = (file) =>
    new Promise((resolve) => {
      if (!file.type.startsWith('image/')) { resolve(file); return; }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          // Cap longest side at 1200px — sufficient for document verification
          const MAX_DIM = 1200;
          if (width > MAX_DIM || height > MAX_DIM) {
            if (width >= height) { height = Math.round((height * MAX_DIM) / width); width = MAX_DIM; }
            else { width = Math.round((width * MAX_DIM) / height); height = MAX_DIM; }
          }
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          // Progressively reduce quality until under per-file target
          let quality = 0.82;
          const attempt = () => {
            canvas.toBlob((blob) => {
              if (blob.size <= PER_FILE_TARGET || quality <= 0.15) {
                resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
              } else { quality = parseFloat((quality - 0.08).toFixed(2)); attempt(); }
            }, 'image/jpeg', quality);
          };
          attempt();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });

  const handleFileUpload = async (field, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setToast({ message: 'File too large. Maximum accepted size is 5MB.', type: 'error' });
      setTimeout(() => setToast(null), 4000);
      return;
    }

    // Always compress images — keeps total request well under server limit
    let finalFile = file;
    if (file.type.startsWith('image/')) {
      setToast({ message: 'Optimising image…', type: 'success' });
      finalFile = await compressImage(file);
      setToast(null);
    }

    if (activeStep === 'identity') {
      setIdentityData(prev => ({ ...prev, [field]: finalFile }));
    } else if (activeStep === 'business') {
      setBusinessData(prev => ({ ...prev, [field]: finalFile }));
    } else if (activeStep === 'address') {
      setAddressData(prev => ({ ...prev, [field]: finalFile }));
    }
  };
  
  const handleIdentityLookup = async () => {
    if (!identityData.idType || !identityData.idNumber) {
      setToast({ message: 'Please select ID type and enter ID number', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    setIsLookingUp(true);
    try {
      const response = await kycAPI.lookupIdentity(identityData.idType, identityData.idNumber);
      if (response && response.success) {
        const data = response.data;
        
        // Auto-fill the fields. Handle date conversion if needed.
        let dob = data.date_of_birth;
        if (dob && dob.includes('T')) {
           dob = dob.split('T')[0];
        }

        setIdentityData(prev => ({
          ...prev,
          firstName: data.first_name || prev.firstName,
          lastName: data.last_name || prev.lastName,
          dateOfBirth: dob || prev.dateOfBirth
        }));
        setToast({ message: 'Details fetched successfully!', type: 'success' });
      } else {
         throw new Error('No data found for this ID');
      }
    } catch (error) {
      setToast({ message: error.message || 'Identity lookup failed', type: 'error' });
    } finally {
      setIsLookingUp(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  const submitIdentityVerification = async () => {
    // Validate identity data
    if (!identityData.idType || !identityData.idNumber || !identityData.firstName ||
        !identityData.lastName || !identityData.dateOfBirth) {
      setToast({ message: 'Please fill all required fields', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    if (!identityData.idFrontImage || !identityData.idBackImage || !identityData.selfieImage) {
      setToast({ message: 'Please upload all required documents', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    setIsLoading(true);
    try {

      const formData = new FormData();
      formData.append('nin', identityData.idNumber);
      formData.append('id_type', identityData.idType);
      formData.append('first_name', identityData.firstName);
      formData.append('last_name', identityData.lastName);
      formData.append('date_of_birth', identityData.dateOfBirth);
      formData.append('identity_front', identityData.idFrontImage);
      formData.append('identity_back', identityData.idBackImage);
      formData.append('selfie', identityData.selfieImage);

      await kycAPI.submitIdentity(formData);

      setVerificationStatus(prev => ({
        ...prev,
        identity: { status: 'pending_review', verified: false }
      }));

      setToast({ message: 'Identity documents submitted for review!', type: 'success' });
      setTimeout(() => {
        setToast(null);
        if (userType === 'seller') {
          setActiveStep('business');
        } else {
          setActiveStep('address');
        }
      }, 2000);
    } catch (error) {
      setToast({ message: error.message || 'Failed to submit identity verification', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Business Verification Functions (for sellers)
  const submitBusinessVerification = async () => {
    if (!businessData.businessType || !businessData.registrationNumber) {
      setToast({ message: 'Please fill all required fields', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    if (!businessData.businessDocument) {
      setToast({ message: 'Please upload business registration document', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    setIsLoading(true);
    try {

      const formData = new FormData();
      formData.append('business_type', businessData.businessType);
      formData.append('registration_number', businessData.registrationNumber);
      formData.append('tax_id', businessData.taxId);
      formData.append('cac', businessData.businessDocument);
      if (businessData.proofOfOwnership) {
        formData.append('proof_of_ownership', businessData.proofOfOwnership);
      }

      await kycAPI.submitBusiness(formData);

      setVerificationStatus(prev => ({
        ...prev,
        business: { status: 'pending_review', verified: false }
      }));

      setToast({ message: 'Business documents submitted for review!', type: 'success' });
      setTimeout(() => {
        setToast(null);
        setActiveStep('address');
      }, 2000);
    } catch (error) {
      setToast({ message: error.message || 'Failed to submit business verification', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Address Verification Functions
  const submitAddressVerification = async () => {
    if (!addressData.addressLine1 || !addressData.city || !addressData.state) {
      setToast({ message: 'Please fill all required fields', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    if (!addressData.proofDocument) {
      setToast({ message: 'Please upload proof of address document', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    setIsLoading(true);
    try {

      const formData = new FormData();
      formData.append('address_street', addressData.addressLine1 + (addressData.addressLine2 ? ', ' + addressData.addressLine2 : ''));
      formData.append('address_city', addressData.city);
      formData.append('address_state', addressData.state);
      formData.append('address_postal_code', addressData.postalCode);
      formData.append('address_country', addressData.country);
      formData.append('proof_of_address', addressData.proofDocument);

      await kycAPI.submitAddress(formData);

      setVerificationStatus(prev => ({
        ...prev,
        address: { status: 'pending_review', verified: false }
      }));

      setToast({ message: 'Address verification submitted successfully!', type: 'success' });
      setTimeout(() => {
        setToast(null);
        if (onComplete) onComplete();
      }, 2000);
    } catch (error) {
      setToast({ message: error.message || 'Failed to submit address verification', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const statusConfig = {
      verified: { icon: faCheckCircle, color: '#22C55E', text: 'Verified' },
      pending: { icon: faClock, color: '#F59E0B', text: 'Pending' },
      pending_review: { icon: faClock, color: '#F59E0B', text: 'Under Review' },
      rejected: { icon: faTimesCircle, color: '#EF4444', text: 'Rejected' },
      not_started: { icon: faExclamationTriangle, color: '#6B7280', text: 'Not Started' }
    };

    const config = statusConfig[status] || statusConfig.not_started;

    return (
      <span className="kyc-status-badge" style={{ color: config.color }}>
        <FontAwesomeIcon icon={config.icon} />
        {config.text}
      </span>
    );
  };

  // Render Email Verification Step
  const renderEmailVerification = () => {
    // Email is already verified from signup — show confirmed state
    if (verificationStatus.email?.verified) {
      return (
        <div className="kyc-step-content">
          <div className="kyc-step-icon" style={{ background: 'linear-gradient(135deg, #22C55E, #16A34A)' }}>
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <h3>Email Verified</h3>
          <div className="kyc-email-verified-box">
            <FontAwesomeIcon icon={faCheckCircle} className="kyc-email-verified-icon" />
            <div>
              <p className="kyc-email-verified-title">Your email is already verified</p>
              <p className="kyc-email-verified-sub">{email || 'Your email address'} was verified during account registration.</p>
            </div>
          </div>
          <button
            className="kyc-btn kyc-btn-primary"
            onClick={() => setActiveStep('identity')}
            style={{ marginTop: '2rem' }}
          >
            Continue to Identity Verification <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.5rem' }} />
          </button>
        </div>
      );
    }

    return (
      <div className="kyc-step-content">
        <div className="kyc-step-icon">
          <FontAwesomeIcon icon={faEnvelope} />
        </div>
        <h3>Email Verification</h3>
        <p className="kyc-step-description">
          Verify your email address to secure your account and receive important notifications.
        </p>

        {!emailSent ? (
          <div className="kyc-form-section">
            <p className="kyc-email-display">We will send a verification code to {email || 'your email'}.</p>
            <button
              className="kyc-btn kyc-btn-primary"
              onClick={sendEmailVerification}
              disabled={isLoading}
            >
              {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Send Verification Code'}
            </button>
          </div>
        ) : (
          <div className="kyc-form-section">
            <div className="kyc-form-group">
              <label>Enter 6-Digit Code</label>
              <input
                type="text"
                maxLength="6"
                className="kyc-code-input"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
              />
            </div>
            <button
              className="kyc-btn kyc-btn-primary"
              onClick={verifyEmailCode}
              disabled={isLoading || emailCode.length !== 6}
            >
              {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Verify Email'}
            </button>

            {countdown > 0 ? (
              <p className="kyc-resend-timer">Resend code in {countdown}s</p>
            ) : (
              <button
                className="kyc-btn kyc-btn-secondary"
                onClick={sendEmailVerification}
                disabled={isLoading}
              >
                Resend Code
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Shared verified/pending state block
  const renderStepDone = (icon, title, statusLabel, statusColor, nextLabel, nextStep) => (
    <div className="kyc-step-content">
      <div className="kyc-step-icon" style={{ background: statusColor === '#22C55E' ? 'linear-gradient(135deg,#22C55E,#16A34A)' : 'linear-gradient(135deg,#F59E0B,#D97706)' }}>
        <FontAwesomeIcon icon={statusColor === '#22C55E' ? faCheckCircle : faClock} />
      </div>
      <h3>{title}</h3>
      <div className="kyc-email-verified-box" style={{ borderColor: statusColor + '55', background: statusColor === '#22C55E' ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)' }}>
        <FontAwesomeIcon icon={statusColor === '#22C55E' ? faCheckCircle : faClock} className="kyc-email-verified-icon" style={{ color: statusColor }} />
        <div>
          <p className="kyc-email-verified-title" style={{ color: statusColor }}>{statusLabel}</p>
          <p className="kyc-email-verified-sub">{statusColor === '#22C55E' ? 'This step has been successfully completed.' : 'Your documents are under review. This usually takes 24–48 hours.'}</p>
        </div>
      </div>
      {nextStep && (
        <button className="kyc-btn kyc-btn-primary" onClick={() => setActiveStep(nextStep)} style={{ marginTop: '2rem' }}>
          {nextLabel} <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.5rem' }} />
        </button>
      )}
    </div>
  );

  // Render Identity Verification Step
  const renderIdentityVerification = () => {
    const status = verificationStatus.identity?.status;
    if (status === 'verified') return renderStepDone(faIdCard, 'Identity Verified', 'Identity verified', '#22C55E', userType === 'seller' ? 'Continue to Business Verification' : 'Continue to Address Verification', userType === 'seller' ? 'business' : 'address');
    if (status === 'pending_review') return renderStepDone(faIdCard, 'Identity Under Review', 'Documents submitted — under review', '#F59E0B', userType === 'seller' ? 'Continue to Business Verification' : 'Continue to Address Verification', userType === 'seller' ? 'business' : 'address');
    return (
    <div className="kyc-step-content">
      <div className="kyc-step-icon">
        <FontAwesomeIcon icon={faIdCard} />
      </div>
      <h3>Identity Verification</h3>
      <p className="kyc-step-description">
        Verify your identity to comply with regulations and build trust with customers.
      </p>

      <div className="kyc-form-section">
        <div className="kyc-form-row">
          <div className="kyc-form-group">
            <label>ID Type *</label>
            <select
              value={identityData.idType}
              onChange={(e) => setIdentityData({ ...identityData, idType: e.target.value })}
            >
              <option value="">Select ID Type</option>
              <option value="nin">National ID (NIN)</option>
              <option value="passport">International Passport</option>
              <option value="drivers_license">Driver's License</option>
              <option value="voters_card">Voter's Card</option>
            </select>
          </div>
          <div className="kyc-form-group">
            <label>ID Number *</label>
            <div className="kyc-input-with-button">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={identityData.idNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  if (identityData.idType === 'nin') {
                    // Only allow digits and max 11 chars
                    if (!/^\d*$/.test(value)) return;
                    if (value.length > 11) return;
                  }
                  setIdentityData({ ...identityData, idNumber: value });
                }}
                maxLength={identityData.idType === 'nin' ? 11 : undefined}
                placeholder={identityData.idType === 'nin' ? "Enter 11-digit NIN" : "Enter ID number"}
              />
              <button 
                type="button" 
                className="kyc-lookup-btn"
                onClick={handleIdentityLookup}
                disabled={isLookingUp || !identityData.idNumber || (identityData.idType === 'nin' && identityData.idNumber.length !== 11)}
              >
                {isLookingUp ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Fetch Details'}
              </button>
            </div>
          </div>
        </div>

        <div className="kyc-form-row">
          <div className="kyc-form-group">
            <label>First Name *</label>
            <input
              type="text"
              value={identityData.firstName}
              onChange={(e) => setIdentityData({ ...identityData, firstName: e.target.value })}
              placeholder="Fetched from ID"
              readOnly
              className="kyc-readonly-input"
            />
          </div>
          <div className="kyc-form-group">
            <label>Last Name *</label>
            <input
              type="text"
              value={identityData.lastName}
              onChange={(e) => setIdentityData({ ...identityData, lastName: e.target.value })}
              placeholder="Fetched from ID"
              readOnly
              className="kyc-readonly-input"
            />
          </div>
        </div>

        <div className="kyc-form-group">
          <label>Date of Birth *</label>
          <input
            type="date"
            value={identityData.dateOfBirth}
            onChange={(e) => setIdentityData({ ...identityData, dateOfBirth: e.target.value })}
            readOnly
            className="kyc-readonly-input"
          />
        </div>

        <div className="kyc-upload-section">
          <h4>Upload Documents</h4>
          <div className="kyc-upload-size-notice">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Max file size: <strong>5MB</strong> per image &nbsp;·&nbsp; Accepted: JPG, PNG &nbsp;·&nbsp; Large images are auto-compressed
          </div>

          <div className="kyc-upload-grid">
            <div className="kyc-upload-item">
              <label className="kyc-upload-label">
                <FontAwesomeIcon icon={faUpload} />
                <span>ID Front *</span>
                <small className="kyc-upload-hint">Max 5MB · JPG/PNG · Auto-compressed</small>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleFileUpload('idFrontImage', e.target.files[0])}
                  hidden
                />
              </label>
              {identityData.idFrontImage && (
                <p className="kyc-file-name">{identityData.idFrontImage.name}</p>
              )}
            </div>

            <div className="kyc-upload-item">
              <label className="kyc-upload-label">
                <FontAwesomeIcon icon={faUpload} />
                <span>ID Back *</span>
                <small className="kyc-upload-hint">Max 5MB · JPG/PNG · Auto-compressed</small>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleFileUpload('idBackImage', e.target.files[0])}
                  hidden
                />
              </label>
              {identityData.idBackImage && (
                <p className="kyc-file-name">{identityData.idBackImage.name}</p>
              )}
            </div>

            <div className="kyc-upload-item">
              <label className="kyc-upload-label">
                <FontAwesomeIcon icon={faCamera} />
                <span>Selfie *</span>
                <small className="kyc-upload-hint">Max 5MB · JPG/PNG · Auto-compressed</small>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleFileUpload('selfieImage', e.target.files[0])}
                  hidden
                />
              </label>
              {identityData.selfieImage && (
                <p className="kyc-file-name">{identityData.selfieImage.name}</p>
              )}
            </div>
          </div>
        </div>

        <button
          className="kyc-btn kyc-btn-primary"
          onClick={submitIdentityVerification}
          disabled={isLoading}
        >
          {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Submit Identity Verification'}
        </button>
      </div>
    </div>
    );
  };

  // Render Business Verification Step (for sellers only)
  const renderBusinessVerification = () => {
    const status = verificationStatus.business?.status;
    if (status === 'verified') return renderStepDone(faBuilding, 'Business Verified', 'Business verified', '#22C55E', 'Continue to Address Verification', 'address');
    if (status === 'pending_review') return renderStepDone(faBuilding, 'Business Under Review', 'Documents submitted — under review', '#F59E0B', 'Continue to Address Verification', 'address');
    return (
    <div className="kyc-step-content">
      <div className="kyc-step-icon">
        <FontAwesomeIcon icon={faBuilding} />
      </div>
      <h3>Business Verification</h3>
      <p className="kyc-step-description">
        Verify your business information to start selling on the platform.
      </p>

      <div className="kyc-form-section">
        <div className="kyc-form-group">
          <label>Business Type *</label>
          <select
            value={businessData.businessType}
            onChange={(e) => setBusinessData({ ...businessData, businessType: e.target.value })}
          >
            <option value="">Select Business Type</option>
            <option value="sole_proprietorship">Sole Proprietorship</option>
            <option value="partnership">Partnership</option>
            <option value="limited_liability">Limited Liability Company (LLC)</option>
            <option value="corporation">Corporation</option>
          </select>
        </div>

        <div className="kyc-form-row">
          <div className="kyc-form-group">
            <label>Business Registration Number *</label>
            <input
              type="text"
              value={businessData.registrationNumber}
              onChange={(e) => setBusinessData({ ...businessData, registrationNumber: e.target.value })}
              placeholder="CAC Registration Number"
            />
          </div>
          <div className="kyc-form-group">
            <label>Tax ID (Optional)</label>
            <input
              type="text"
              value={businessData.taxId}
              onChange={(e) => setBusinessData({ ...businessData, taxId: e.target.value })}
              placeholder="TIN"
            />
          </div>
        </div>

        <div className="kyc-upload-section">
          <h4>Upload Business Documents</h4>
          <div className="kyc-upload-size-notice">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Max file size: <strong>5MB</strong> per file &nbsp;·&nbsp; Accepted: JPG, PNG, PDF &nbsp;·&nbsp; Images are auto-compressed
          </div>

          <div className="kyc-upload-grid">
            <div className="kyc-upload-item">
              <label className="kyc-upload-label">
                <FontAwesomeIcon icon={faFileAlt} />
                <span>CAC Certificate *</span>
                <small className="kyc-upload-hint">Max 5MB · JPG/PNG/PDF · Images auto-compressed</small>
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleFileUpload('businessDocument', e.target.files[0])}
                  hidden
                />
              </label>
              {businessData.businessDocument && (
                <p className="kyc-file-name">{businessData.businessDocument.name}</p>
              )}
            </div>

            <div className="kyc-upload-item">
              <label className="kyc-upload-label">
                <FontAwesomeIcon icon={faFileAlt} />
                <span>Proof of Ownership (Optional)</span>
                <small className="kyc-upload-hint">Max 5MB · JPG/PNG/PDF · Images auto-compressed</small>
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleFileUpload('proofOfOwnership', e.target.files[0])}
                  hidden
                />
              </label>
              {businessData.proofOfOwnership && (
                <p className="kyc-file-name">{businessData.proofOfOwnership.name}</p>
              )}
            </div>
          </div>
        </div>

        <button
          className="kyc-btn kyc-btn-primary"
          onClick={submitBusinessVerification}
          disabled={isLoading}
        >
          {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Submit Business Verification'}
        </button>
      </div>
    </div>
    );
  };

  // Render Address Verification Step
  const renderAddressVerification = () => {
    const status = verificationStatus.address?.status;
    if (status === 'verified') return renderStepDone(faMapMarkerAlt, 'Address Verified', 'Address verified', '#22C55E', null, null);
    if (status === 'pending_review') return renderStepDone(faMapMarkerAlt, 'Address Under Review', 'Documents submitted — under review', '#F59E0B', null, null);
    return (
    <div className="kyc-step-content">
      <div className="kyc-step-icon">
        <FontAwesomeIcon icon={faMapMarkerAlt} />
      </div>
      <h3>Address Verification</h3>
      <p className="kyc-step-description">
        Verify your business address for shipping and customer trust.
      </p>

      <div className="kyc-form-section">
        <div className="kyc-form-group">
          <label>Address Line 1 *</label>
          <input
            type="text"
            value={addressData.addressLine1}
            onChange={(e) => setAddressData({ ...addressData, addressLine1: e.target.value })}
            placeholder="Street address"
          />
        </div>

        <div className="kyc-form-group">
          <label>Address Line 2</label>
          <input
            type="text"
            value={addressData.addressLine2}
            onChange={(e) => setAddressData({ ...addressData, addressLine2: e.target.value })}
            placeholder="Apartment, suite, etc. (optional)"
          />
        </div>

        <div className="kyc-form-row">
          <div className="kyc-form-group">
            <label>City *</label>
            <input
              type="text"
              value={addressData.city}
              onChange={(e) => setAddressData({ ...addressData, city: e.target.value })}
              placeholder="City"
            />
          </div>
          <div className="kyc-form-group">
            <label>State *</label>
            <input
              type="text"
              value={addressData.state}
              onChange={(e) => setAddressData({ ...addressData, state: e.target.value })}
              placeholder="State"
            />
          </div>
        </div>

        <div className="kyc-form-row">
          <div className="kyc-form-group">
            <label>Postal Code</label>
            <input
              type="text"
              value={addressData.postalCode}
              onChange={(e) => setAddressData({ ...addressData, postalCode: e.target.value })}
              placeholder="Postal code"
            />
          </div>
          <div className="kyc-form-group">
            <label>Country *</label>
            <select
              value={addressData.country}
              onChange={(e) => setAddressData({ ...addressData, country: e.target.value })}
            >
              <option value="Nigeria">Nigeria</option>
              <option value="Ghana">Ghana</option>
              <option value="Kenya">Kenya</option>
              <option value="South Africa">South Africa</option>
            </select>
          </div>
        </div>

        <div className="kyc-upload-section">
          <h4>Proof of Address</h4>
          <p className="kyc-help-text">
            Upload a recent utility bill, bank statement, or government document showing your address (dated within last 3 months)
          </p>
          <div className="kyc-upload-size-notice">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Max file size: <strong>5MB</strong> per file &nbsp;·&nbsp; Accepted: JPG, PNG, PDF &nbsp;·&nbsp; Images are auto-compressed
          </div>

          <div className="kyc-upload-item">
            <label className="kyc-upload-label">
              <FontAwesomeIcon icon={faFileAlt} />
              <span>Upload Document *</span>
              <small className="kyc-upload-hint">Max 5MB · JPG/PNG/PDF · Images auto-compressed</small>
              <input
                type="file"
                accept=".pdf,image/jpeg,image/png,image/jpg"
                onChange={(e) => handleFileUpload('proofDocument', e.target.files[0])}
                hidden
              />
            </label>
            {addressData.proofDocument && (
              <p className="kyc-file-name">{addressData.proofDocument.name}</p>
            )}
          </div>
        </div>

        <button
          className="kyc-btn kyc-btn-primary"
          onClick={submitAddressVerification}
          disabled={isLoading}
        >
          {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Complete Verification'}
        </button>
      </div>
    </div>
    );
  };

  return (
    <div className={isDarkMode ? 'dark-mode' : ''}>
    <div className="kyc-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`kyc-toast kyc-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="kyc-header">
        <div className="kyc-header-icon">
          <FontAwesomeIcon icon={faShieldAlt} />
        </div>
        <h2>KYC Verification</h2>
        <p>Complete verification to unlock all platform features</p>
      </div>

      {/* Progress Steps */}
      <div className="kyc-progress">
        <div
          className={`kyc-progress-step ${verificationStatus.email?.verified ? 'completed' : activeStep === 'email' ? 'active' : ''}`}
          onClick={() => setActiveStep('email')}
        >
          <div className="kyc-progress-icon">
            <FontAwesomeIcon icon={faEnvelope} />
          </div>
          <span>Email</span>
          {renderStatusBadge(verificationStatus.email?.status)}
        </div>

        <div
          className={`kyc-progress-step ${verificationStatus.identity?.verified ? 'completed' : activeStep === 'identity' ? 'active' : ''}`}
          onClick={() => verificationStatus.email?.verified && setActiveStep('identity')}
        >
          <div className="kyc-progress-icon">
            <FontAwesomeIcon icon={faIdCard} />
          </div>
          <span>Identity</span>
          {renderStatusBadge(verificationStatus.identity?.status)}
        </div>

        {userType === 'seller' && (
          <div
            className={`kyc-progress-step ${verificationStatus.business?.verified ? 'completed' : activeStep === 'business' ? 'active' : ''}`}
            onClick={() => verificationStatus.identity?.status === 'pending_review' && setActiveStep('business')}
          >
            <div className="kyc-progress-icon">
              <FontAwesomeIcon icon={faBuilding} />
            </div>
            <span>Business</span>
            {renderStatusBadge(verificationStatus.business?.status)}
          </div>
        )}

        <div
          className={`kyc-progress-step ${verificationStatus.address?.verified ? 'completed' : activeStep === 'address' ? 'active' : ''}`}
          onClick={() => {
            if (userType === 'seller') {
              verificationStatus.business?.status === 'pending_review' && setActiveStep('address');
            } else {
              verificationStatus.identity?.status === 'pending_review' && setActiveStep('address');
            }
          }}
        >
          <div className="kyc-progress-icon">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
          </div>
          <span>Address</span>
          {renderStatusBadge(verificationStatus.address?.status)}
        </div>
      </div>

      {/* Step Content */}
      <div className="kyc-content">
        {activeStep === 'email' && renderEmailVerification()}
        {activeStep === 'identity' && renderIdentityVerification()}
        {activeStep === 'business' && userType === 'seller' && renderBusinessVerification()}
        {activeStep === 'address' && renderAddressVerification()}
      </div>
    </div>
    </div>
  );
};

export default KYCVerification;
